/*
 * @GestionalePD Calendar event dialog UI
 */
import * as React from 'react';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Grid,
  Typography,
  Box,
  DialogActions,
  DialogContent,
  Switch,
  FormControlLabel,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import { useStandDocumentUpload } from '../../../hooks/useStandDocumentUpload';
import {
  DefaultContainedButton,
  DefaultContainedLoadingButton,
  DefaultOutlinedButton,
  RedOutlinedButton,
  RedOutlinedLoadingButton,
} from '../../Buttons';
import { CustomAutocomplete, CustomTextField } from '../../StyledInputs';
import API from '../../../api';

const StandHandlerDialog = forwardRef(
  (
    {
      openDialog,
      handleClose,
      createStand,
      updateStand,
      deleteStand,
      standToHandle,
      handlerType,
      event,
      setSnackbarText,
      showSnackbar,
      refreshStandsList,
    },
    ref
  ) => {
    const hasStandToHandle = handlerType !== 'create' && standToHandle && Object.keys(standToHandle).length > 0;

    const [standTitle, setStandTitle] = useState('');
    const [standDescription, setStandDescription] = useState('');
    const [standNumber, setStandNumber] = useState('');
    const [standVolunters, setStandVolunters] = useState(false);
    const [standCelebration, setStandCelebration] = useState('');
    const [standType, setStandType] = useState({});
    const [restaurantTypes, setRestaurantTypes] = useState([]);
    const [documentLabel, setDocumentLabel] = useState('');

    const {
      document,
      setDocument,
      uploading,
      handleUpload,
      handleDelete,
      deleting,
      selectedItem,
      setSelectedItem,
      cdnUrl,
    } = useStandDocumentUpload({
      refreshList: refreshStandsList,
      showSnackbar,
      setSnackbarText,
    });
    const fileRef = useRef();

    const [errors, setErrors] = useState({
      title: false,
      standType: false,
      restaurantTypes: false,
    });

    const validateForm = () => {
      const newErrors = {
        title: standTitle.trim() === '',
        standType: !standType || !standType.id,
        restaurantTypes:
          standType?.name?.toLowerCase() === 'ristorante' &&
          (!Array.isArray(restaurantTypes) || restaurantTypes.length === 0),
      };

      setErrors(newErrors);

      return !Object.values(newErrors).some((v) => v);
    };

    const [standTypesList, setStandTypesList] = useState([]);
    const [restaurantTypesList, setRestaurantTypesList] = useState([]);

    const getStandTypesList = () => {
      API.get('/celeb_stand_types').then((response) => {
        setStandTypesList(response.data);
      });
    };

    const getRestaurantTypesList = () => {
      API.get('/celeb_stand_resttypes').then((response) => {
        setRestaurantTypesList(response.data);
      });
    };

    useEffect(() => {
      if (standType && Object.keys(standType).length > 0 && standType.name.toLowerCase() === 'ristorante') {
        getRestaurantTypesList();
      }
    }, [standType]);

    useEffect(() => {
      if (openDialog) {
        if (handlerType === 'create') {
          setStandTitle('');
          setStandDescription('');
          setStandNumber('');
          setStandVolunters(false);
          setStandCelebration('');
          setStandType({});
          setRestaurantTypes([]);
        } else if (hasStandToHandle) {
          setStandTitle(standToHandle.title || '');
          setStandDescription(standToHandle.description || '');
          setStandNumber(standToHandle.number || '');
          setStandVolunters(!!standToHandle.volunters);
          setStandCelebration(standToHandle.celebration_id || '');
          setStandType(standToHandle.stand_type || {});
          setRestaurantTypes(standToHandle.stand_rest_types || []);
        }

        getStandTypesList();
      }
    }, [openDialog, handlerType, standToHandle, event]);

    useEffect(() => {
      if (openDialog && handlerType === 'update' && standToHandle) {
        setSelectedItem(standToHandle);
      }
    }, [openDialog, handlerType, standToHandle]);

    const onCreateStand = () => {
      if (!validateForm()) return;

      const data = {
        title: standTitle,
        description: standDescription,
        stand_type_id: standType.id,
        stand_rest_types: restaurantTypes || [],
        number: standNumber,
        volunters: standVolunters,
        celebration_id: hasStandToHandle ? standCelebration : event.id,
      };

      createStand(data);
    };

    const onUpdateStand = () => {
      if (!validateForm()) return;
      const data = {
        id: standToHandle.id,
        title: standTitle,
        description: standDescription,
        number: standNumber,
        stand_type_id: standType.id,
        stand_rest_types: restaurantTypes || [],
        volunters: Number(standVolunters),
        celebration_id: hasStandToHandle ? standCelebration : event.id,
      };

      if (selectedItem.document_url) {
        data.document_label = documentLabel;
      }

      updateStand(data);
    };

    const onDeleteStand = () => {
      const standId = standToHandle.id;
      deleteStand(standId);
    };

    const refreshStandForm = () => {
      setStandTitle('');
      setStandDescription('');
      setStandNumber('');
      setStandVolunters(false);
      setStandCelebration('');
    };

    useImperativeHandle(ref, () => ({
      refreshStandForm,
    }));

    const extractFileName = (url) => {
      if (!url) return '';
      return url.substring(url.lastIndexOf('/') + 1);
    };

    const copyLink = (url) => {
      navigator.clipboard.writeText(cdnUrl(url));
      showSnackbar(true, 'Link del documento copiato negli appunti');
    };

    return (
      <Dialog onClose={handleClose} open={openDialog} fullWidth>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ padding: '12px' }}>
          <Typography variant="h5">
            {handlerType === 'create'
              ? 'Creazione Stand'
              : handlerType === 'update'
              ? 'Modifica Stand'
              : 'Elimina Stand'}
          </Typography>
        </Box>
        {handlerType !== 'delete' ? (
          <DialogContent dividers>
            <Grid container sx={{ padding: '24px' }} spacing={3}>
              <Grid item xs={12}>
                <CustomTextField
                  label="Titolo"
                  value={standTitle}
                  onChange={(e) => setStandTitle(e.currentTarget.value)}
                  multiline
                  required
                  error={errors.title}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomTextField
                  label="Descrizione"
                  value={standDescription}
                  onChange={(e) => setStandDescription(e.currentTarget.value)}
                  multiline
                  minRows={5}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomAutocomplete
                  options={standTypesList}
                  required
                  renderInput={(params) => <TextField {...params} label="Tipo Stand *" error={errors.standType} />}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={standType}
                  onChange={(event, newValue) => {
                    setStandType(newValue);
                    setErrors((prev) => ({ ...prev, standType: false }));
                  }}
                />
              </Grid>

              {Object.keys(standType).length !== 0 && standType.name.toLowerCase() === 'ristorante' && (
                <Grid item xs={12}>
                  <CustomAutocomplete
                    multiple
                    disableCloseOnSelect
                    options={restaurantTypesList}
                    renderInput={(params) => (
                      <TextField {...params} label="Tipo Ristorante *" error={errors.restaurantTypes} />
                    )}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    value={restaurantTypes}
                    onChange={(event, value) => {
                      setRestaurantTypes(value);
                      setErrors((prev) => ({ ...prev, restaurantTypes: false }));
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <CustomTextField
                  label="Numero Stand"
                  value={standNumber}
                  onChange={(e) => setStandNumber(e.currentTarget.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={standVolunters} onChange={() => setStandVolunters(!standVolunters)} />}
                  label={standVolunters ? 'Gestito da volontari' : 'Non gestito da volontari'}
                />
              </Grid>

              {handlerType === 'create' && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    È possibile allegare un file con le informazioni dello stand solo dopo averlo creato.
                  </Alert>
                </Grid>
              )}

              {handlerType === 'update' && (
                <>
                  <Grid item xs={12}>
                    <input
                      ref={fileRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={(e) => setDocument(e.target.files[0])}
                      accept="image/*,application/pdf"
                    />
                    <Typography fontWeight="bold">Gestione Documento</Typography>
                    <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {document
                          ? `File selezionato: ${document.name}`
                          : selectedItem?.document_url
                          ? `File caricato: ${extractFileName(selectedItem.document_url)}`
                          : 'Nessun file selezionato'}
                      </Typography>

                      {!selectedItem?.document_url && (
                        <>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <DefaultOutlinedButton disabled={uploading} onClick={() => fileRef.current?.click()}>
                              Seleziona File
                            </DefaultOutlinedButton>
                            <DefaultContainedLoadingButton
                              loading={uploading}
                              onClick={() => handleUpload('celebration_stands')}
                              disabled={
                                !document ||
                                (selectedItem?.document_url &&
                                  document.name === extractFileName(selectedItem.document_url))
                              }
                            >
                              Carica
                            </DefaultContainedLoadingButton>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            La dimensione massima consentita è di 10MB.
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            File consentiti: PDF, JPG, JPEG, PNG.
                          </Typography>
                        </>
                      )}

                      {selectedItem?.document_url && !document && (
                        <Stack direction="row" spacing={2}>
                          <a
                            href={cdnUrl(selectedItem?.document_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            <DefaultOutlinedButton>Visualizza</DefaultOutlinedButton>
                          </a>

                          <DefaultContainedButton onClick={() => copyLink(selectedItem?.document_url)}>
                            Copia Link
                          </DefaultContainedButton>

                          <RedOutlinedLoadingButton
                            disabled={uploading}
                            loading={deleting}
                            color="error"
                            onClick={async () => {
                              await handleDelete('celebration_stands');
                              setSelectedItem((prev) => ({ ...prev, document_url: null }));
                              setDocument(null);
                            }}
                          >
                            Elimina
                          </RedOutlinedLoadingButton>
                        </Stack>
                      )}
                    </Stack>
                  </Grid>

                  {selectedItem?.document_url && (
                    <Grid item xs={12}>
                      <CustomTextField
                        label="Etichetta Documento"
                        value={documentLabel}
                        onChange={(e) => setDocumentLabel(e.currentTarget.value)}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </DialogContent>
        ) : (
          <DialogContent>
            <Box>
              <Typography>Sei sicuro di voler eliminare lo stand {standToHandle.name}?</Typography>
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <DefaultOutlinedButton onClick={handleClose}>Chiudi</DefaultOutlinedButton>
          {handlerType !== 'delete' ? (
            <DefaultContainedButton onClick={handlerType === 'create' ? onCreateStand : onUpdateStand}>
              Salva
            </DefaultContainedButton>
          ) : (
            <RedOutlinedButton onClick={onDeleteStand}>Elimina</RedOutlinedButton>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

export default StandHandlerDialog;

// Calendar Dialog parameter type declaration
StandHandlerDialog.propTypes = {
  openDialog: PropTypes.bool,
  handleClose: PropTypes.func,
};
